import React, {Suspense, useEffect, useMemo, useState} from 'react';
import Tabla from '../../../components/ui/materialui/serverSideTable';
import TableHeader from '../../../components/ui/materialui/tableHeader';
import {useLocation, useNavigate} from "react-router-dom";
import * as yup from 'yup'
import { FiltroCartera, FiltroCarteraCompleto } from '../models/filtroCartera';
import validaRut from '../../../utils/validaRut';
import { useForm } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup'
import { useColumns } from '../utils/columns';
import { useDataFetch } from '../../../hooks/useDataFetch2';
import ConfirmationModal from '../../../components/ui/materialui/confirmationModal';
import { Box, Grid2, Paper, Typography } from '@mui/material';
import CustomAccordion from '../../../components/ui/materialui/customAccordion';
import InputNumber from '../../../components/inputs/yup/materialui/inputNumber';
import InputRut from '../../../components/inputs/yup/materialui/inputRut';
import InputText from '../../../components/inputs/yup/materialui/inputText';
import InputSelect from '../../../components/inputs/yup/materialui/inputSelect';
import InputFecha from '../../../components/inputs/yup/materialui/inputFecha';
import MButton from '../../../components/inputs/yup/materialui/mButton';
import SummarizeTwoToneIcon from '@mui/icons-material/SummarizeTwoTone';
import FilterListIcon from '@mui/icons-material/FilterList';
import { TablaBasicFilter } from '../../../utils/tableBasicFilter';
import Conversiones from '../../../utils/conversiones';
import Validaciones from '../../../utils/validaciones';
import { exportExcel } from '../../../utils/exportExcel';
import InputMultiSelect from '../../../components/inputs/yup/materialui/inputMultiSelect';
import Breadcrumb from '../../../components/ui/materialui/breadcrumb';
import { PermissionGuard } from '../../../components/auth/ProtectedRoute';
import { TipoFuncionalidad } from '../../../utils/enums';


const AlertaCustom = React.lazy(() => import("../../../components/ui/alertaCustom"));

function CarteraTotal() {
    const [urlApiBandeja, setUrlApiBandeja] = useState<string>(`siscred-api-cartera-total/v1/cartera/total`)
    const [filtros, setFiltros] = useState<FiltroCarteraCompleto | TablaBasicFilter>({
        numeroSolicitud: null,
        numeroOperacion: null,
        rut: null,
        nombre: null,
        fechaInicio: null,
        fechaFin: null,
        estados: [],
        sort: null,
        order: null,
        take: null,
        page: null,
        search: null
    })
    const [call, setCall] = useState<boolean>(false);
    const [callFilter, setCallFilter] = useState<boolean>(false);

    const validationSchema: yup.ObjectSchema<FiltroCartera> = yup.object().shape({
        numeroSolicitud: yup.number()
            .transform((value) => Number.isNaN(value) ? null : value)
            .nullable(),
        numeroOperacion: yup.string().default('').nullable().test('valida-numero', 'N° de Operacion Inválido',
            function(value) {
                if (!value) return true
                return Validaciones.esLetrasNumeros(value)
            }
        ),
        rut: yup.string().default('').nullable().test('valida-rut', 'RUT inválido', function (value) {
            return validaRut.rutFormat(value);
        }).max(12, 'Máximo 12 caractéres'),
        nombre: yup.string().default('').nullable().max(60, 'Máximo 60 caractéres'),
        estados: yup.array().required('Requerido').test('valida-largo', 'Requerido', function (value) {
            return value && value.length > 0
        }),
        fechaInicio: yup.date().test(
            'is-valid',
            'El intervalo de fechas debe ser válido',
            function (value) {
              const { fechaFin } = this.parent;
              return (!value && !fechaFin) || (fechaFin && value && new Date(value) < new Date(fechaFin));
            }
        ).notRequired(),
        fechaFin: yup.date().notRequired()
    })
    
    const {
        register,
        formState: { errors, isDirty },
        handleSubmit,
        reset,
        setValue,
        clearErrors,
        control,
        watch,
        trigger
    } = useForm<FiltroCartera>({ resolver: yupResolver(validationSchema)
        , mode: 'onBlur',
        defaultValues: {           
            estados: ["ACTIVADO", "LIQUIDADO", "ENDOSADO"],
        
        }
     })

    const startDate = watch("fechaFin");
    useEffect(() => {
        trigger("fechaInicio");
    }, [startDate, trigger]);

    const [modalConfirmacion, setModalConfirmacion] = useState({
        show: false,
        message: '',
        metodo: () => {
        },
    })
    
    const { COLUMNS } = useColumns({ control, filtros });
    const columns = useMemo(() => COLUMNS, [control, filtros]);
    const controlTabla = useDataFetch(
        urlApiBandeja,
        new URLSearchParams('filters.estados=[ACTIVADO]'),
        true,
    )

    const location = useLocation();
    const { estado } = location.state || {};
    const navigate = useNavigate();

    let filtrosGuardados: FiltroCarteraCompleto | null | undefined = estado;

    const onSubmit = (data: FiltroCartera) => filtrar(data);

    function filtrar(data: FiltroCartera){
        if (data.numeroSolicitud) {
            controlTabla.manejarCambiosFiltros(
                'filters.numero_solicitud',
                data.numeroSolicitud.toString(),
            );

            setFiltros((prev) => ({
                ...prev,
                numeroSolicitud: data.numeroSolicitud
            }));
        } else {
            controlTabla.manejarCambiosFiltros('filters.numero_solicitud', '');

            setFiltros((prev) => ({
                ...prev,
                numeroSolicitud: null
            }));
        }

        if (data.numeroOperacion) {
            controlTabla.manejarCambiosFiltros(
                'filters.numero_operacion',
                data.numeroOperacion.toString(),
            );

            setFiltros((prev) => ({
                ...prev,
                numeroOperacion: data.numeroOperacion
            }));
        } else {
            controlTabla.manejarCambiosFiltros('filters.numero_operacion', '');

            setFiltros((prev) => ({
                ...prev,
                numeroOperacion: null
            }));
        }

        if (data.rut) {
            controlTabla.manejarCambiosFiltros(
                'filters.rut_deudor',
                data.rut.toString(),
            );

            setFiltros((prev) => ({
                ...prev,
                rut: data.rut
            }));
        } else {
            controlTabla.manejarCambiosFiltros('filters.rut_deudor', '');

            setFiltros((prev) => ({
                ...prev,
                rut: ''
            }));
        }

        if (data.nombre) {
            controlTabla.manejarCambiosFiltros(
                'filters.nombre_deudor',
                data.nombre.toString(),
            );

            setFiltros((prev) => ({
                ...prev,
                nombre: data.nombre
            }));
        } else {
            controlTabla.manejarCambiosFiltros('filters.nombre_deudor', '');

            setFiltros((prev) => ({
                ...prev,
                nombre: ''
            }));
        }

        if (data.estados) {
            controlTabla.manejarCambiosFiltros(
                'filters.estados',
                "["+data.estados.join(",")+"]",
            )
            setFiltros((prev) => ({
                ...prev,
                estados: data.estados
            }));
        } else {
            controlTabla.manejarCambiosFiltros(
                'filters.estado_vigente',
                '[]',
            )
            setFiltros((prev) => ({
                ...prev,
                estados: ''
            }));
        }

        if (data.fechaInicio) {
            controlTabla.manejarCambiosFiltros(
                'filters.fecha_inicio',
                Conversiones.fechaToStringInput(data.fechaInicio),
            )
            setFiltros((prev) => ({
                ...prev,
                fechaInicio: data.fechaInicio
            }));
        } else {
            controlTabla.manejarCambiosFiltros(
                'filters.fecha_inicio',
                '',
            )
            setFiltros((prev) => ({
                ...prev,
                fechaInicio: ''
            }));
        }

        if (data.fechaFin) {
            controlTabla.manejarCambiosFiltros(
                'filters.fecha_fin',
                Conversiones.fechaToStringInput(data.fechaFin),
            )
            setFiltros((prev) => ({
                ...prev,
                fechaFin: data.fechaFin
            }));
        } else {
            controlTabla.manejarCambiosFiltros(
                'filters.fecha_fin',
                '',
            )
            setFiltros((prev) => ({
                ...prev,
                fechaFin: ''
            }));
        }

        setCallFilter(true);
    }

    useEffect(() => {
        if (callFilter && !call) {
            controlTabla.fetchData();
            setCallFilter(false);
        }
    }, [callFilter]);

    useEffect(() => {
        const handleBeforeUnload = () => {
            filtrosGuardados = null;
            navigate(location.pathname, { replace: true, state: {} });
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);

    const breadcrumbItems = [
        { label: 'Inicio', url: '/' },
        { label: 'Cartera mutuos', active: true },
    ]

    return(<>
        <Suspense>
            <ConfirmationModal
                onHide={() =>
                    setModalConfirmacion({ ...modalConfirmacion, show: false })
                }
                onConfirm={modalConfirmacion.metodo}
                show={modalConfirmacion.show}
                message={modalConfirmacion.message}
            />

            <Breadcrumb items={breadcrumbItems} />

            <Box sx={{ mt: 2 }}>
                <Paper elevation={3} sx={{ p: 3 }}>
                    <CustomAccordion
                        title='Filtros'
                        expandedDefault
                    >
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <Grid2 container spacing={2}>
                                <Grid2 size={8}>
                                    <InputMultiSelect
                                        name="estados"
                                        control={control}
                                        options={[
                                            { value: "ACTIVADO", label: "Activado" },
                                            { value: "LIQUIDADO", label: "Liquidado" },
                                            { value: "ENDOSADO", label: "Endosado" },
                                            { value: "DESACTIVADO", label: "Desactivado" },
                                            { value: "PREPAGADO", label: "Prepagado" },
                                            { value: "EXTINGUIDO", label: "Extinguido" }
                                        ]}
                                        label="Estados"
                                        onChange={(value) => setValue("estados", value ?? "")}
                                    />
                                    <Typography color="error">{errors.estados?.message}</Typography>
                                </Grid2>
                                <Grid2 size={4}>
                                    <InputRut name="rut" control={control} label="RUT" />
                                    <Typography color="error">{errors.rut?.message}</Typography>
                                </Grid2>     
                            </Grid2>

                            <Grid2 container spacing={2} sx={{ marginTop: 1 }}>
                                <Grid2 size={4}>
                                    <InputText name="nombre" control={control} label="Nombre" />
                                    <Typography color="error">{errors.nombre?.message}</Typography>
                                </Grid2> 
                                <Grid2 size={4}>
                                    <InputNumber name="numeroSolicitud" control={control} label="N° de Solicitud" />
                                    <Typography color="error">{errors.numeroSolicitud?.message}</Typography>
                                </Grid2>  
                                <Grid2 size={4}>
                                    <InputText name="numeroOperacion" control={control} label="N° de Operación" />
                                    <Typography color="error">{errors.numeroOperacion?.message}</Typography>
                                </Grid2> 
                            </Grid2>
                            <Grid2 container spacing={2} sx={{marginTop: 1}}>
                                <Grid2 size={2}>
                                    <InputFecha 
                                        name="fechaInicio"
                                        control={control}
                                        label="Rango de Fecha de Firma"
                                    />
                                    <Typography color="error">{errors.fechaInicio?.message}</Typography>
                                </Grid2>
                                <Grid2 size={2}>
                                    <InputFecha 
                                        name="fechaFin"
                                        control={control}
                                        label=" "
                                    />
                                </Grid2>
                                <Grid2 size={4}/>
                                <PermissionGuard permissions={[TipoFuncionalidad.Cartera_ver]}>
                                <Grid2 size={4} sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                                    <MButton type="submit" variant="contained" color="primary" startIcon={<FilterListIcon />}>
                                        Filtrar
                                    </MButton>
                                </Grid2>
                                </PermissionGuard>
                            </Grid2>
                        </form>
                    </CustomAccordion>

                    <Box sx={{ mt: 4 }}>
                        <Suspense>
                            <Tabla
                                columns={columns}
                                control={controlTabla}
                                url={urlApiBandeja}
                                callApi={false}
                                setFiltros={setFiltros}
                                titulo={<TableHeader
                                    title=""
                                    control={controlTabla}
                                    setFiltros={setFiltros}
                                    searchFilter={filtrosGuardados?.search}
                                    buttonExport={
                                        <>
                                        <PermissionGuard permissions={[TipoFuncionalidad.Transversal_exportar]}>
                                            <MButton
                                                type="button"
                                                variant="outlined"
                                                onClick={() => exportExcel(controlTabla.data, columns, "CARTERA")}
                                                startIcon={<SummarizeTwoToneIcon />}
                                            >
                                                Exportar
                                            </MButton>
                                        </PermissionGuard>
                                        </>
                                    }
                                />}
                            />
                        </Suspense>
                    </Box>
                </Paper>
           </Box>
        </Suspense> 
    </>)
}

export default CarteraTotal;
